/**
 * JSON → XML Tool
 */

import { XMLBuilder } from 'fast-xml-parser';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';
import { parseJsonLenient } from '@/lib/jsonParser';

export const JsonToXmlTool: ITransformerTool = {
  id: 'json-to-xml',
  name: 'JSON → XML',
  description: 'Convert JSON to XML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['json', 'xml', 'convert', 'transform'],

  supportedInputTypes: [SEMANTIC_TYPES.JSON],
  outputType: SEMANTIC_TYPES.XML,

  attributes: [
    {
      name: 'rootName',
      type: 'string',
      label: 'Root Element',
      defaultValue: 'root',
      description: 'Name of the root XML element',
    },
    {
      name: 'pretty',
      type: 'boolean',
      label: 'Pretty Print',
      defaultValue: true,
      description: 'Indent and format the XML output',
    },
    {
      name: 'indentBy',
      type: 'select',
      label: 'Indent',
      defaultValue: '  ',
      options: ['  ', '    ', '\t'],
      description: 'Indentation string for pretty output',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const rootName = String(config.rootName ?? 'root').trim() || 'root';
    const pretty = config.pretty !== false;
    const indentBy = (config.indentBy as string) || '  ';

    let parsed: unknown;
    try {
      parsed = parseJsonLenient(String(input ?? ''));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Invalid JSON';
      throw ToolError.validation(`Invalid JSON: ${msg}`);
    }

    try {
      const builder = new XMLBuilder({
        ignoreAttributes: false,
        format: pretty,
        indentBy,
        suppressEmptyNode: true,
      });

      const wrapped: Record<string, unknown> = { [rootName]: parsed };
      return builder.build(wrapped);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to build XML';
      throw ToolError.execution(msg);
    }
  },
};
